import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import SEOHead from "@/components/site/SEOHead";
import Reveal from "@/components/site/Reveal";
import SmartMedia from "@/components/site/SmartMedia";
import { hasMedia } from "@/lib/media";
import { CustomSection } from "@/components/site/PropertySections";
import DraftBanner from "@/components/site/DraftBanner";
import { breadcrumbs } from "@/lib/schema";
import { useEnquiry } from "@/components/site/PublicLayout";
import { trackEvent } from "@/lib/settings";

export default function CustomPage() {
  const { slug } = useParams();
  const enquiry = useEnquiry();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null); setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/public/pages/${slug}`).then((r) => {
      setPage(r.data);
      trackEvent("custom_page_view", { slug });
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-x pt-40 pb-32 text-center">
        <div className="overline">404</div>
        <h1 className="display-2 mt-4">Page not found</h1>
        <Link to="/" className="link-arrow mt-8 inline-flex">Back to home <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }
  if (!page) return <div className="pt-32 pb-32 container-x"><div className="animate-pulse h-96 bg-bone" /></div>;

  let schema = null;
  if (page.schema_json) {
    try { schema = typeof page.schema_json === "string" ? JSON.parse(page.schema_json) : page.schema_json; } catch (e) { schema = null; }
  }
  if (!schema) {
    schema = { "@context": "https://schema.org", "@type": "WebPage", name: page.hero?.headline || page.title, description: page.meta_description };
  }
  schema = [schema, breadcrumbs([{ name: "Home", path: "/" }, { name: page.title || page.slug, path: `/${page.slug}` }])];

  const hero = page.hero || {};
  const hasHeroImg = hasMedia(hero.image_url);
  const heroHasContent = hero.headline || hero.subheadline || hero.overline || hasHeroImg;

  return (
    <>
      <SEOHead
        title={page.meta_title || page.title}
        description={page.meta_description}
        keywords={page.keywords}
        ogImage={page.og_image || hero.image_url}
        schema={schema}
      />
      <DraftBanner show={page.is_published === false} />

      {heroHasContent && (
        <section className="relative pt-44 md:pt-52 pb-20 md:pb-28 overflow-hidden" data-testid="custom-hero">
          {hasHeroImg && (
            <>
              <SmartMedia media={hero.image_url} alt={hero.headline || page.title} className="absolute inset-0 w-full h-full object-cover" eager />
              <div className="absolute inset-0 bg-ink/60" />
            </>
          )}
          <div className={`relative container-x ${hasHeroImg ? "text-ivory" : ""}`}>
            {hero.overline && <Reveal><div className={`overline ${hasHeroImg ? "text-ivory/80" : "accent-color"}`}>{hero.overline}</div></Reveal>}
            <Reveal delay={150}>
              <h1 className={`font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mt-8 max-w-4xl ${hasHeroImg ? "text-ivory" : ""}`}>
                {hero.headline || page.title}
              </h1>
            </Reveal>
            {hero.subheadline && (
              <Reveal delay={250}>
                <p className={`body-lead mt-8 max-w-2xl ${hasHeroImg ? "text-ivory/85" : ""}`}>{hero.subheadline}</p>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {(page.layout || []).filter((s) => s.enabled !== false).map((sec) => (
        <CustomSection key={sec.id} sec={sec} enquiry={enquiry} slug={slug} />
      ))}
    </>
  );
}
